import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import NotificationBell from "../common/NotificationBell";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import { toast } from "sonner";
import {
  LayoutDashboard, UserCircle, ClipboardList, LogOut, Menu, X,
  Calendar, IndianRupee, Download, Moon, Sun,
  Wallet, Receipt, BookOpen, BarChart3, FileText, MessageSquare,
  CreditCard, ShieldAlert, TrendingUp, Settings, Users,
  BellRing, WifiOff, CloudOff, RefreshCw, Shield, PauseCircle, SplitSquareHorizontal,
  Upload, BanknoteIcon, Banknote, FileSpreadsheet, ClipboardCheck, Trophy, Award,
  Fingerprint, GraduationCap, Lock, Briefcase, Building2,
} from "lucide-react";

/* ─── Mobile Nav ──────────────────────────────────────────────────────────── */
const mobileNavKeys = {
  superadmin: [
    { section: "Main" },
    { key: "nav.dashboard", icon: LayoutDashboard, path: "/app/dashboard" },
    { key: "nav.users", icon: Users, path: "/app/users" },
    { key: "nav.customers", icon: UserCircle, path: "/app/customers" },
    { key: "nav.proposals", icon: ClipboardList, path: "/app/proposals" },
    { key: "nav.disbursements", icon: BanknoteIcon, path: "/app/disbursements" },
    { key: "nav.paymentVerification", icon: Wallet, path: "/app/payment-verification" },
    { key: "nav.collections", icon: Receipt, path: "/app/collections" },
    { key: "nav.collectionIntelligence", icon: ShieldAlert, path: "/app/collection-intelligence" },
    { section: "Accounting" },
    { key: "Acc. Dashboard", icon: BarChart3, path: "/app/accounting-dashboard" },
    { key: "Bank Accounts", icon: Building2, path: "/app/bank-accounts" },
    { key: "Cash Accounts", icon: Banknote, path: "/app/cash-accounts" },
    { key: "Journal Entries", icon: FileText, path: "/app/journal-entries" },
    { key: "Overdue Waivers", icon: Shield, path: "/app/overdue-waivers" },
    { key: "Interest Only", icon: PauseCircle, path: "/app/interest-only-accounts" },
    { key: "Part Payment", icon: SplitSquareHorizontal, path: "/app/part-payment-controls" },
    { section: "Operations" },
    { key: "nav.paymentChannels", icon: CreditCard, path: "/app/payment-channels" },
    { key: "nav.analytics", icon: TrendingUp, path: "/app/analytics" },
    { key: "nav.agentPerformance", icon: TrendingUp, path: "/app/agent-performance" },
    { key: "nav.bulkImport", icon: Upload, path: "/app/bulk-import" },
    { key: "Import Staging", icon: FileSpreadsheet, path: "/app/import-batches" },
    { key: "Data Quality", icon: ClipboardCheck, path: "/app/data-quality" },
    { section: "Commissions" },
    { key: "Commission Rules", icon: Settings, path: "/app/commission-settings" },
    { key: "Commission Ledger", icon: Award, path: "/app/commission-ledger" },
    { key: "Agent Ledger", icon: Briefcase, path: "/app/agent-ledger" },
    { key: "Leaderboard", icon: Trophy, path: "/app/leaderboard" },
    { section: "Portfolio" },
    { key: "Loan Portfolio", icon: BarChart3, path: "/app/portfolio" },
    { section: "Accounting Suite" },
    { key: "nav.customerLedger", icon: BookOpen, path: "/app/customer-ledger" },
    { key: "Chart of Accounts", icon: BookOpen, path: "/app/accounting/chart-of-accounts" },
    { key: "Bank Accounts", icon: Building2, path: "/app/accounting/bank-accounts" },
    { key: "Journal Entries", icon: ClipboardList, path: "/app/accounting/journal-entries" },
    { key: "Ledger", icon: BookOpen, path: "/app/accounting/ledger" },
    { key: "P&L", icon: TrendingUp, path: "/app/accounting/pnl" },
    { key: "Balance Sheet", icon: Receipt, path: "/app/accounting/balance-sheet" },
    { key: "Bank Statement", icon: CreditCard, path: "/app/accounting/bank-statement" },
    { key: "Exports", icon: Download, path: "/app/accounting/exports" },
    { key: "Acct Audit", icon: ShieldAlert, path: "/app/accounting/audit-logs" },
    { section: "System" },
    { key: "nav.settings", icon: Settings, path: "/app/settings" },
    { key: "System Health", icon: Shield, path: "/app/system-health" },
    { key: "Duplicate Resolution", icon: Fingerprint, path: "/app/duplicate-resolution" },
    { key: "nav.auditLogs", icon: FileText, path: "/app/audit" },
    { key: "nav.smsLogs", icon: MessageSquare, path: "/app/sms" },
    { key: "nav.training", icon: GraduationCap, path: "/app/training" },
  ],
  admin: [
    { section: "Main" },
    { key: "nav.dashboard", icon: LayoutDashboard, path: "/app/dashboard" },
    { key: "nav.users", icon: Users, path: "/app/users" },
    { key: "nav.customers", icon: UserCircle, path: "/app/customers" },
    { key: "nav.proposals", icon: ClipboardList, path: "/app/proposals" },
    { key: "nav.paymentVerification", icon: Wallet, path: "/app/payment-verification" },
    { key: "nav.collections", icon: Receipt, path: "/app/collections" },
    { key: "nav.collectionIntelligence", icon: ShieldAlert, path: "/app/collection-intelligence" },
    { key: "nav.agentPerformance", icon: TrendingUp, path: "/app/agent-performance" },
    { key: "nav.analytics", icon: BarChart3, path: "/app/analytics" },
    { key: "nav.bulkImport", icon: Upload, path: "/app/bulk-import" },
    { section: "Commissions" },
    { key: "Commission Ledger", icon: Award, path: "/app/commission-ledger" },
    { key: "Agent Ledger", icon: Briefcase, path: "/app/agent-ledger" },
    { key: "Leaderboard", icon: Trophy, path: "/app/leaderboard" },
    { section: "Portfolio & Accounting" },
    { key: "nav.customerLedger", icon: BookOpen, path: "/app/customer-ledger" },
    { key: "Loan Portfolio", icon: BarChart3, path: "/app/portfolio" },
    { key: "Chart of Accounts", icon: BookOpen, path: "/app/accounting/chart-of-accounts" },
    { key: "Bank Accounts", icon: Building2, path: "/app/accounting/bank-accounts" },
    { key: "Journal Entries", icon: ClipboardList, path: "/app/accounting/journal-entries" },
    { key: "Ledger", icon: BookOpen, path: "/app/accounting/ledger" },
    { key: "P&L", icon: TrendingUp, path: "/app/accounting/pnl" },
    { key: "Balance Sheet", icon: Receipt, path: "/app/accounting/balance-sheet" },
    { key: "Bank Statement", icon: CreditCard, path: "/app/accounting/bank-statement" },
    { key: "Exports", icon: Download, path: "/app/accounting/exports" },
    { section: "System" },
    { key: "nav.auditLogs", icon: FileText, path: "/app/audit" },
    { key: "nav.smsLogs", icon: MessageSquare, path: "/app/sms" },
    { key: "nav.training", icon: GraduationCap, path: "/app/training" },
  ],
  agent: [
    { key: "nav.dashboard", icon: LayoutDashboard, path: "/app/dashboard" },
    { key: "nav.myCustomers", icon: UserCircle, path: "/app/customers" },
    { key: "nav.proposals", icon: ClipboardList, path: "/app/proposals" },
    { key: "nav.collectionIntelligence", icon: ShieldAlert, path: "/app/collection-intelligence" },
    { key: "nav.bulkImport", icon: Upload, path: "/app/bulk-import" },
    { section: "Commissions" },
    { key: "My Commissions", icon: Award, path: "/app/my-commissions" },
    { key: "Agent Ledger", icon: Briefcase, path: "/app/agent-ledger" },
    { key: "Leaderboard", icon: Trophy, path: "/app/leaderboard" },
    { section: "" },
    { key: "nav.training", icon: GraduationCap, path: "/app/training" },
  ],
  customer: [
    { key: "nav.myLoans", icon: LayoutDashboard, path: "/app/customer-portal" },
    { key: "nav.emiSchedule", icon: Calendar, path: "/app/customer-portal/schedule" },
    { key: "nav.payEMI", icon: IndianRupee, path: "/app/customer-portal/pay" },
    { key: "nav.myPayments", icon: Receipt, path: "/app/customer-portal/my-payments" },
    { key: "nav.statements", icon: Download, path: "/app/customer-portal/statements" },
    { key: "nav.ledger", icon: BookOpen, path: "/app/customer-portal/ledger" },
    { key: "nav.training", icon: GraduationCap, path: "/app/training" },
  ],
};

/* ─── Page Titles ─────────────────────────────────────────────────────────── */
const pageTitles = {
  "/app/dashboard": "Dashboard",
  "/app/users": "User Management",
  "/app/customers": "Customers",
  "/app/customers/new": "New Customer",
  "/app/proposals": "Loan Proposals",
  "/app/payment-verification": "Payment Verification",
  "/app/collections": "Collections",
  "/app/collection-intelligence": "Collection Intelligence",
  "/app/agent-performance": "Agent Performance",
  "/app/analytics": "Analytics",
  "/app/payment-channels": "Payment Channels",
  "/app/overdue-waivers": "Overdue Waivers",
  "/app/interest-only-accounts": "Interest Only Accounts",
  "/app/part-payment-controls": "Part Payment Controls",
  "/app/settings": "Settings",
  "/app/audit": "Audit Logs",
  "/app/sms": "SMS Logs",
  "/app/training": "Training Center",
  "/app/bulk-import": "Bulk Import",
  "/app/portfolio": "Loan Portfolio",
  "/app/accounting-dashboard": "Accounting Dashboard",
  "/app/bank-accounts": "Bank Accounts",
  "/app/cash-accounts": "Cash Accounts",
  "/app/journal-entries": "Journal Entries",
  "/app/disbursements": "Disbursements",
  "/app/import-batches": "Import Batches",
  "/app/data-quality": "Data Quality",
  "/app/commission-settings": "Commission Rules",
  "/app/commission-ledger": "Commission Ledger",
  "/app/my-commissions": "My Commissions",
  "/app/leaderboard": "Leaderboard",
  "/app/system-health": "System Health",
  "/app/duplicate-resolution": "Duplicate Resolution",
  "/app/change-password": "Change Password",
  "/app/agent-ledger": "Agent Ledger",
  "/app/customer-ledger": "Customer Ledger",
  "/app/customer-portal": "My Loans",
  "/app/customer-portal/schedule": "EMI Schedule",
  "/app/customer-portal/pay": "Pay EMI",
  "/app/customer-portal/my-payments": "My Payments",
  "/app/customer-portal/statements": "Statements",
  "/app/customer-portal/ledger": "Ledger",
  "/app/accounting/chart-of-accounts": "Chart of Accounts",
  "/app/accounting/bank-accounts": "Bank Accounts",
  "/app/accounting/journal-entries": "Journal Entries",
  "/app/accounting/ledger": "Ledger",
  "/app/accounting/pnl": "Profit & Loss",
  "/app/accounting/balance-sheet": "Balance Sheet",
  "/app/accounting/bank-statement": "Bank Statement",
  "/app/accounting/exports": "Exports",
  "/app/accounting/audit-logs": "Accounting Audit",
};

/* ─── Topbar (desktop) ────────────────────────────────────────────────────── */
function Topbar() {
  const { user } = useAuth();
  const location = useLocation();
  const title = pageTitles[location.pathname] || "Dashboard";

  return (
    <div
      data-testid="topbar"
      className="hidden md:flex items-center justify-between h-14 px-4 lg:px-6"
      style={{
        borderBottom: "1px solid var(--fintech-border)",
        background: "var(--fintech-sidebar)",
      }}
    >
      <h2 className="font-heading font-semibold text-sm lg:text-base ft-text truncate">{title}</h2>
      <div className="flex items-center gap-2 lg:gap-3">
        <NotificationBell />
        <div className="flex items-center gap-2 pl-2 lg:pl-3" style={{ borderLeft: "1px solid var(--fintech-border)" }}>
          <div className="h-7 w-7 lg:h-8 lg:w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[10px] lg:text-xs font-bold">{user?.full_name?.charAt(0)?.toUpperCase() || "U"}</span>
          </div>
          <div className="hidden lg:block">
            <p className="text-[13px] font-medium leading-none ft-text">{user?.full_name}</p>
            <p className="text-[11px] mt-0.5 capitalize ft-text-muted">{user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Layout ─────────────────────────────────────────────────────────── */
export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t, lang, setLang, LANG_LABELS, languages } = useLanguage();
  const { dark, toggle } = useTheme();
  const { supported: pushSupported, subscribed: pushSubscribed, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotifications();
  const { isOnline, pendingCount, syncing, syncNow } = useNetworkStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const prevOnlineRef = useRef(true);

  const mobileItems = mobileNavKeys[user?.role] || [];

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (prevOnlineRef.current && !isOnline) {
      toast.warning("You are offline. Data will be saved locally.", { duration: 4000 });
    }
    if (!prevOnlineRef.current && isOnline) {
      toast.success("Back online! Syncing pending data...", { duration: 3000 });
    }
    prevOnlineRef.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") {
      setShowInstallBanner(false);
      setInstallPrompt(null);
    }
  };

  const mobileTitle = pageTitles[location.pathname] || "Shubh Finance";

  return (
    <div className="min-h-screen flex" style={{ background: "var(--fintech-bg)" }}>
      <Sidebar />

      {/* Offline Banner */}
      {!isOnline && (
        <div
          data-testid="offline-banner"
          className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-3 py-1.5 sm:py-2 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium shadow-lg md:left-[240px]"
        >
          <WifiOff size={14} />
          <span>You are offline</span>
          {pendingCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded-full text-[10px] font-bold">
              {pendingCount} pending
            </span>
          )}
        </div>
      )}

      {/* Sync Banner */}
      {isOnline && pendingCount > 0 && (
        <div
          data-testid="sync-pending-banner"
          className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white px-3 py-1.5 sm:py-2 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium shadow-lg md:left-[240px]"
        >
          <CloudOff size={14} />
          <span>{pendingCount} pending</span>
          <button
            data-testid="sync-now-btn"
            onClick={async () => {
              const result = await syncNow();
              if (result) {
                if (result.synced > 0) toast.success(`Synced ${result.synced} collection(s)`);
                if (result.failed > 0) toast.error(`${result.failed} failed to sync`);
              }
            }}
            disabled={syncing}
            className="ml-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded-lg text-[10px] sm:text-xs font-bold flex items-center gap-1 disabled:opacity-60"
          >
            <RefreshCw size={11} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing..." : "Sync"}
          </button>
        </div>
      )}

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div
          data-testid="pwa-install-banner"
          className="md:hidden fixed top-14 left-0 right-0 z-40 bg-blue-600 text-white px-3 py-2.5 flex items-center justify-between shadow-lg"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs">S</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-semibold truncate">Install Shubh Finance</p>
              <p className="text-[10px] text-blue-200">Quick access from home screen</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button data-testid="pwa-install-btn" onClick={handleInstall}
              className="px-2.5 py-1 bg-white text-blue-600 text-[10px] sm:text-xs font-bold rounded-lg">
              Install
            </button>
            <button onClick={() => setShowInstallBanner(false)} className="text-blue-200 hover:text-white p-0.5">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 z-30 flex items-center justify-between px-3"
        style={{ background: "var(--fintech-sidebar)", borderBottom: "1px solid var(--fintech-border)" }}>
        <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} className="ft-text-secondary p-1">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <span className="font-heading font-bold text-xs sm:text-sm flex items-center gap-1.5 ft-text truncate max-w-[50%]">
          <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-[10px]">S</span>
          </div>
          <span className="truncate">{mobileTitle}</span>
        </span>
        <div className="flex items-center gap-0.5">
          <NotificationBell />
          <button data-testid="mobile-logout" onClick={() => { logout(); navigate("/login"); }} className="ft-text-secondary p-1">
            <LogOut size={16} />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setMobileOpen(false)}>
          <div
            className="w-[280px] max-w-[85vw] h-full shadow-2xl flex flex-col overflow-hidden"
            style={{ background: "var(--fintech-sidebar)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Menu Header */}
            <div className="p-3 flex items-center justify-between flex-shrink-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <span className="text-white font-bold text-xs">S</span>
                </div>
                <p className="font-heading font-bold text-sm text-white">{t("app.name")}</p>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1">
                <X size={16} className="text-slate-500" />
              </button>
            </div>

            {/* User card */}
            <div className="px-3 py-2.5 flex-shrink-0" style={{ background: "rgba(255,255,255,0.02)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[10px] font-bold">{user?.full_name?.charAt(0)?.toUpperCase() || "U"}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-200 truncate">{user?.full_name || user?.username}</p>
                  <p className="text-[10px] text-slate-500 capitalize">{user?.role}</p>
                </div>
              </div>
            </div>

            {/* Nav Items */}
            <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {mobileItems.map((item, idx) =>
                item.section !== undefined ? (
                  item.section === "" ? (
                    <div key={`divider-${idx}`} className="pt-2 mt-1" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }} />
                  ) : (
                    <p key={`section-${idx}`} className="text-[9px] font-bold text-slate-500 uppercase tracking-wider px-2 pt-3 pb-1">
                      {item.section}
                    </p>
                  )
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`mobile-nav-${item.key}`}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all
                      ${isActive ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white" : "text-slate-400 hover:bg-white/[0.05] hover:text-slate-200"}`
                    }
                  >
                    <item.icon size={14} className="flex-shrink-0" />
                    <span className="truncate">{t(item.key)}</span>
                  </NavLink>
                )
              )}
            </nav>

            {/* Bottom controls */}
            <div className="p-2 space-y-1.5 flex-shrink-0" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
              <div className="flex gap-1">
                {languages.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`flex-1 py-1 text-[10px] rounded-lg font-medium transition-all ${
                      l === lang ? "bg-blue-600 text-white" : "bg-white/[0.05] text-slate-400"
                    }`}
                  >
                    {LANG_LABELS[l]}
                  </button>
                ))}
              </div>

              <div className="flex gap-1">
                <button onClick={toggle}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] text-slate-400 hover:bg-white/[0.05]">
                  {dark ? <Sun size={12} /> : <Moon size={12} />}
                  {dark ? "Light" : "Dark"}
                </button>

                <NavLink to="/app/change-password" onClick={() => setMobileOpen(false)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] text-slate-400 hover:bg-white/[0.05]">
                  <Lock size={12} /> Password
                </NavLink>
              </div>

              {pushSupported && (
                <button
                  data-testid="mobile-push-toggle"
                  onClick={async () => {
                    if (pushSubscribed) await pushUnsubscribe();
                    else await pushSubscribe();
                  }}
                  className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] transition-all ${
                    pushSubscribed ? "text-blue-400 bg-blue-500/10" : "text-slate-400 hover:bg-white/[0.05]"
                  }`}
                >
                  <BellRing size={12} />
                  {pushSubscribed ? "Notifications On" : "Enable Notifications"}
                </button>
              )}

              <button
                data-testid="mobile-logout-btn"
                onClick={() => { logout(); navigate("/login"); setMobileOpen(false); }}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] text-red-400 hover:bg-red-500/10"
              >
                <LogOut size={12} />
                {t("nav.logout")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content area */}
      <main className="flex-1 md:ml-[240px] min-h-screen min-h-[100dvh] flex flex-col">
        <Topbar />
        <div className={`flex-1 overflow-y-auto pt-14 md:pt-0 ${showInstallBanner ? "pt-[104px]" : ""} ${(!isOnline || (isOnline && pendingCount > 0)) ? "mt-8 sm:mt-10" : ""}`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
